<template>
  <van-popup
      v-model:show="model"
      :position="$i18n.locale === 'en' ? 'right' : 'left'"
      :style="{ width: !$device.isDesktop ? '100%' : '500px', height: '100%' }"
      @closed="onClose"
      @opened="onOpened"
  >
    <div class="full-height flex flex-col overflow-hidden bg-light-grey">
      <van-nav-bar :title="$t('product.reviews.customers_reviews')" @click="onClose">

        <template v-if="$i18n.locale === 'en'" #right>
          <van-icon name="arrow" size="22"/>
        </template>
        <template v-else #left>
          <van-icon name="arrow" size="22"/>
        </template>
      </van-nav-bar>

      <div class="full-height flex flex-col overflow-y-auto">
        <full-page-loader :loading="loading && page === 1"/>
        <div class="flex items-center gap-5 bg-white p-3 my-1">
          <niceone-image :src="product.thumb" height="60px" width="60px"/>
          <div class="flex flex-col space-y-2">
            <h5 class="text-sm font-light">{{ product.name }} {{ product.id }}</h5>
            <p class="flex items-center gap-2">
              <van-rate :model-value="reviewsObj?.average" :size="16" allow-half color="#997adb" readonly
                        void-color="#eee"
                        void-icon="star"/>
              <div class="font-bold text-base" dir="ltr">{{ reviewsObj?.average }} / 5</div>
              <div class="font-light text-xs text-spanish-gray">
                {{ $t("product.reviews.total_reviews", {total: reviewsObj?.review_total}) }}
              </div>
            </p>
          </div>
        </div>
        <div class="flex justify-between items-center my-4 px-4">
          <select-field v-model="sortModel" :block="false" :options="sortOptionsReview"
                        :placeholder="$t('product.reviews.please_select_option')" @change="onSortChange"/>
          <van-button v-if="!reviewsObj?.is_review"
                      class="!bg-secondary-light font-niceone-medium !h-10 !rounded-lg !text-secondary !border-none"
                      type="primary"
                      @click="emit('update:addReviewModel',true)">
            {{ $t('product.reviews.add_review') }}
          </van-button>
        </div>
        <div class="px-2 flex flex-col mb-6">
          <review-list-item v-for="(review,index) in reviews" :key="`review-${index}`" :review="review"
                            class="bg-white"/>
        </div>
        <infinite-load-more v-model="page" :items-length="reviews?.length" :loading="loading" :total="totalReviews"
                            @change="getReviews"/>
      </div>
    </div>
  </van-popup>
</template>

<script setup>
import {getReviewsByProductId} from "@/plugins/API/home"
import SelectField from "@/components/form/SelectField"
import ReviewListItem from "@/components/product/reviews/ReviewListItem"
import InfiniteLoadMore from "~/components/common/InfiniteLoadMore"
import {track} from "~/composables/useTrackEvent"
import {TRACKERS} from "~/constants/events"

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  addReviewModel: {
    type: Boolean,
    default: false
  },
  reviewsObj: {
    type: Object,
    default: {}
  },
  product: {
    type: Object,
    default: {}
  }
})

const {t} = useI18n()
const rating = ref(5)
const page = ref(1)
const limit = ref(10)
const sort = ref('desc')
const reviews = ref([])
const totalReviews = ref(0)
const sortOptionsReview = ref([
  {
    name: t('product.reviews.recently_added_first'),
    value: 'desc'
  },
  {
    name: t('product.reviews.highest_rating_first'),
    value: 'desc_rating'
  }
])
const emit = defineEmits(['update:modelValue', 'update:addReviewModel'])

const model = defineModel()
const sortModel = computed({
  get() {
    return sort.value
  },
  set(val) {
    sort.value = val
  }
})

const getProductId = computed(() => props.product?.id)
const {isLoading: loading, mutateAsync: mutateAsyncReviewsByProductId} = getReviewsByProductId()
const getReviews = () => {
  mutateAsyncReviewsByProductId({
    id: getProductId.value,
    offset: page.value,
    limit: limit.value,
    sort: sort.value
  }).then(({data}) => {
    totalReviews.value = Number(data?.review_total) || 0
    reviews.value = [...reviews.value, ...data?.reviews]
  })
}

const onSortChange = () => {
  reviews.value = []
  page.value = 1
  getReviews()
}

const onOpened = () => {
  getReviews()
}

const onClose = () => {
  totalReviews.value = 0
  reviews.value = []
  page.value = 1
  model.value = false
}

watch(sortModel, (val) => {
  track(TRACKERS.REVIEW_SORT, {eventLabel: val, product: props.product})
})
</script>

<style lang="scss" scoped>

</style>